import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch, type Control } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import { fetchUnidades, unidadeKeys } from '@/services/unidadeService';
import { createUsuario, fetchUsuarios, updateUsuario, usuarioKeys } from '@/services/usuarioService';
import { parseApiError } from '@/utils/apiError';
import { getEffectiveTenantId, TENANT_SCOPE_CHANGED_EVENT } from '@/utils/tenantScope';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { buildUsuarioFormSchema, usuariosSearchSchema, type TUsuarioFormData, type TUsuariosSearchFormData } from './schema';
import type { TUserRole } from '@/types/auth';
import type { IUnidade } from '@/types/unidade';
import type { IUsuario, IUsuarioCreatePayload, IUsuarioUpdatePayload } from '@/types/usuario';

export const Route = createFileRoute('/_authenticated/admin/usuarios/')({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) {
      return;
    }

    const role = context.auth.user?.role;
    if (role !== 'super_admin' && role !== 'tenant_admin' && role !== 'unidade_admin') {
      toast.error('Você não tem permissão para acessar esta tela.');
      return redirect({ to: '/dashboard' });
    }
  },
  component: UsuariosCrud,
});

const ROLE_LABELS: Record<TUserRole, string> = {
  super_admin: 'Super Admin',
  tenant_admin: 'Administrador da Empresa',
  unidade_admin: 'Administrador da Unidade',
  operator: 'Operador',
  viewer: 'Visualizador',
};

const ASSIGNABLE_ROLES: Record<TUserRole, TUserRole[]> = {
  super_admin: ['super_admin', 'tenant_admin', 'unidade_admin', 'operator', 'viewer'],
  tenant_admin: ['tenant_admin', 'unidade_admin', 'operator', 'viewer'],
  unidade_admin: ['operator', 'viewer'],
  operator: [],
  viewer: [],
};

const emptyUser: TUsuarioFormData = {
  senha: '',
  email: '',
  nome: '',
  role: '',
  ativo: true,
  unidade_ids: [],
};

function UsuariosCrud() {
  const { user: authUser } = useAuth();
  const authRole = authUser?.role ?? 'viewer';
  const isSuperAdmin = authRole === 'super_admin';
  const roleOptions = useMemo(() => ASSIGNABLE_ROLES[authRole] ?? [], [authRole]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isEditing = editingId !== null;

  // Empresa/unidade são escolhidas no seletor do Navbar — só reage ao valor.
  const [selectedTenantId, setSelectedTenantId] = useState<string>(() => getEffectiveTenantId() ?? '');
  useEffect(() => {
    const onScopeChanged = () => setSelectedTenantId(getEffectiveTenantId() ?? '');
    document.addEventListener(TENANT_SCOPE_CHANGED_EVENT, onScopeChanged);
    return () => document.removeEventListener(TENANT_SCOPE_CHANGED_EVENT, onScopeChanged);
  }, []);

  const queryClient = useQueryClient();
  const tenantScopeKey = isSuperAdmin ? selectedTenantId : null;

  const { control: searchControl, register: registerSearch } = useForm<TUsuariosSearchFormData>({
    resolver: zodResolver(usuariosSearchSchema),
    defaultValues: { search: '' },
  });
  const search = useWatch({ control: searchControl, name: 'search' });

  const { data: users = [], isLoading, isError, error } = useQuery({
    queryKey: usuarioKeys.list(tenantScopeKey),
    queryFn: fetchUsuarios,
    enabled: !isSuperAdmin || Boolean(selectedTenantId),
  });

  const filteredUsers = useMemo(() => {
    const q = search?.trim().toLowerCase() ?? '';
    if (!q) return users;
    return users.filter((u) => (u.nome || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, search]);

  const { data: unidades = [], isLoading: unidadesLoading, isError: unidadesError } = useQuery({
    queryKey: [...unidadeKeys.all, 'usuarios-form', tenantScopeKey],
    queryFn: () => fetchUnidades({ skip: 0, limit: 0 }),
    enabled: !isSuperAdmin || Boolean(selectedTenantId),
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<TUsuarioFormData>({
    resolver: zodResolver(buildUsuarioFormSchema(isEditing)),
    mode: 'onChange',
    defaultValues: emptyUser,
  });

  const singleUnidadeSelectLocked = showForm && !isEditing && unidades.length === 1 && !unidadesLoading && !unidadesError;

  useEffect(() => {
    if (!singleUnidadeSelectLocked) return;
    setValue('unidade_ids', [String(unidades[0].id)]);
  }, [singleUnidadeSelectLocked, unidades, setValue]);

  const createMutation = useMutation({
    mutationFn: (payload: IUsuarioCreatePayload) => createUsuario(payload),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IUsuarioUpdatePayload }) => updateUsuario(id, payload),
  });
  const saving = createMutation.isPending || updateMutation.isPending;

  const openCreate = () => {
    setEditingId(null);
    reset({ ...emptyUser, role: roleOptions[0] ?? '' });
    setShowForm(true);
  };
  const openEdit = (usuario: IUsuario) => {
    setEditingId(usuario.id);
    reset({
      senha: '',
      email: usuario.email || '',
      nome: usuario.nome || '',
      role: usuario.role,
      ativo: usuario.ativo,
      unidade_ids: usuario.unidade_ids,
    });
    setShowForm(true);
  };
  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
  };

  const onSubmit = async (data: TUsuarioFormData) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: editingId,
          payload: {
            nome: data.nome,
            role: data.role as TUserRole,
            ativo: data.ativo,
            unidade_ids: data.unidade_ids,
            ...(data.senha ? { senha: data.senha } : {}),
          },
        });
      } else {
        await createMutation.mutateAsync({
          email: data.email,
          nome: data.nome,
          senha: data.senha,
          role: data.role as TUserRole,
          unidade_ids: data.unidade_ids.length > 0 ? data.unidade_ids : undefined,
        });
      }

      toast.success(isEditing ? 'Usuário atualizado com sucesso.' : 'Usuário criado com sucesso.');
      setShowForm(false);
      reset(emptyUser);
      await queryClient.invalidateQueries({ queryKey: usuarioKeys.all });
    } catch (err) {
      toast.error(parseApiError(err, isEditing ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário'));
    }
  };

  const listErrorMessage = isError ? parseApiError(error, 'Erro ao listar usuários') : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Usuários</h1>
        <p className="text-on-surface-variant">Gerencie os usuários do sistema.</p>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant p-4">
          <h2 className="text-lg font-semibold text-on-surface">Lista de Usuários</h2>
          <div className="flex items-center gap-3">
            <Input
              className="w-64"
              placeholder="Buscar (nome, email)…"
              autoComplete="off"
              {...registerSearch('search')}
            />
            <Button
              className="bg-success text-on-primary hover:bg-success/90"
              type="button"
              onClick={openCreate}
              disabled={isSuperAdmin && !selectedTenantId}
            >
              Novo Usuário
            </Button>
          </div>
        </div>

        <div className="p-4">
          {isSuperAdmin && !selectedTenantId && (
            <div className="mb-4 rounded-lg bg-surface-container-lowest p-4 text-on-surface-variant">
              Selecione uma empresa no topo da página para listar os usuários.
            </div>
          )}
          {listErrorMessage && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
            >
              {listErrorMessage}
            </div>
          )}
          {isLoading && (
            <LoadingSpinner
              text="Carregando..."
              size="small"
            />
          )}
          {!isLoading && (!isSuperAdmin || Boolean(selectedTenantId)) && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Papel</TableHead>
                  <TableHead>Unidades</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="w-24">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.nome || '-'}</TableCell>
                    <TableCell>{u.email || '-'}</TableCell>
                    <TableCell>{ROLE_LABELS[u.role] ?? u.role}</TableCell>
                    <TableCell>{u.unidade_ids.length || '-'}</TableCell>
                    <TableCell>{u.ativo ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => openEdit(u)}
                        disabled={saving}
                        title="Editar"
                      >
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="p-6 text-center text-on-surface-variant"
                    >
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        modal={true}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field data-invalid={!!errors.email}>
                  <FieldLabel htmlFor="email">E-mail *</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    disabled={isEditing}
                    aria-invalid={!!errors.email}
                    {...register('email')}
                  />
                  <FieldError errors={errors.email ? [errors.email] : undefined} />
                </Field>

                <Field data-invalid={!!errors.senha}>
                  <FieldLabel htmlFor="senha">{isEditing ? 'Senha' : 'Senha *'}</FieldLabel>
                  <Input
                    id="senha"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={!!errors.senha}
                    {...register('senha')}
                  />
                  {isEditing && (
                    <FieldDescription>Deixe em branco para manter a senha atual.</FieldDescription>
                  )}
                  <FieldError errors={errors.senha ? [errors.senha] : undefined} />
                </Field>

                <Field data-invalid={!!errors.nome}>
                  <FieldLabel htmlFor="nome">Nome</FieldLabel>
                  <Input
                    id="nome"
                    autoComplete="name"
                    aria-invalid={!!errors.nome}
                    {...register('nome')}
                  />
                  <FieldError errors={errors.nome ? [errors.nome] : undefined} />
                </Field>

                <Field data-invalid={!!errors.role}>
                  <FieldLabel htmlFor="role">Papel *</FieldLabel>
                  <Controller
                    control={control}
                    name="role"
                    render={({ field }) => (
                      <Select
                        value={field.value || undefined}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="role"
                          aria-invalid={!!errors.role}
                        >
                          <SelectValue placeholder="Selecione o papel" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {roleOptions.map((role) => (
                              <SelectItem
                                key={role}
                                value={role}
                              >
                                {ROLE_LABELS[role]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FieldError errors={errors.role ? [errors.role] : undefined} />
                </Field>

                {isEditing && (
                  <Field orientation="horizontal">
                    <Controller
                      control={control}
                      name="ativo"
                      render={({ field }) => (
                        <Checkbox
                          id="ativo"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      )}
                    />
                    <FieldLabel
                      htmlFor="ativo"
                      className="font-normal"
                    >
                      Ativo
                    </FieldLabel>
                  </Field>
                )}

                <Field data-invalid={!!errors.unidade_ids}>
                  <FieldLabel htmlFor="unidade_ids">Unidade</FieldLabel>
                  <UnidadeMultiCombobox
                    control={control}
                    unidades={unidades}
                    disabled={singleUnidadeSelectLocked}
                  />
                  {unidadesLoading && <FieldDescription>Carregando unidades...</FieldDescription>}
                  {unidadesError && (
                    <FieldDescription
                      role="alert"
                      className="text-destructive"
                    >
                      Erro ao carregar unidades
                    </FieldDescription>
                  )}
                  <FieldError errors={errors.unidade_ids ? [errors.unidade_ids] : undefined} />
                </Field>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={saving || !isValid}
                >
                  {saving ? (isEditing ? 'Salvando...' : 'Criando...') : isEditing ? 'Salvar' : 'Criar'}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancelar
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UnidadeMultiCombobox({
  control,
  unidades,
  disabled,
}: {
  control: Control<TUsuarioFormData>;
  unidades: IUnidade[];
  disabled: boolean;
}) {
  const anchor = useComboboxAnchor();

  return (
    <Controller
      control={control}
      name="unidade_ids"
      render={({ field }) => {
        const selected = unidades.filter((unidade) => field.value.includes(String(unidade.id)));

        return (
          <Combobox
            multiple
            items={unidades}
            itemToStringLabel={(unidade: IUnidade) => unidade.nome || 'Sem nome'}
            value={selected}
            onValueChange={(values: IUnidade[]) => field.onChange(values.map((unidade) => String(unidade.id)))}
            disabled={disabled}
          >
            <ComboboxChips ref={anchor}>
              <ComboboxValue>
                {(values: IUnidade[]) => (
                  <>
                    {values.map((unidade) => (
                      <ComboboxChip key={unidade.id}>{unidade.nome || 'Sem nome'}</ComboboxChip>
                    ))}
                    <ComboboxChipsInput
                      id="unidade_ids"
                      placeholder={values.length === 0 ? 'Selecione uma unidade' : undefined}
                    />
                  </>
                )}
              </ComboboxValue>
            </ComboboxChips>
            <ComboboxContent anchor={anchor}>
              <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
              <ComboboxList>
                {(unidade: IUnidade) => (
                  <ComboboxItem
                    key={unidade.id}
                    value={unidade}
                  >
                    {unidade.nome || 'Sem nome'}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        );
      }}
    />
  );
}
